import { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PeoplePageContainer from '../../shared/peoplePageContainer';
import NotificationCard from '../../shared/notificationCard';
import CustomButton from '../../shared/customButton';
import ConditionalTooltip from '../../shared/conditionalTooltip';
import SectionTitle from '../../shared/sectionTitle';
import GridTable from '../../shared/gridTable';
import CustomFormDrawer from '../../shared/customFormDrawer';
import AbsenceCards from '../../shared/absenceCards';
import StatusIndicator from '../../shared/statusIndicator';
import Tooltip from '../../shared/tooltip';
import DotsMenu from '../../shared/dotsMenu';
import ActionButton from '../../shared/actionButton';
import AlertDialog from '../../shared/alertDialog';
import SelectField from '../../shared/selectField';
import ListWithTooltip from '../../shared/listWithTooltip';
import RichTextPreview from '../../shared/richTextPreview';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  ABSENCE_REQUEST_FIELD_NAMES,
  ABSENCE_FIELD_NAMES,
} from '../../../constants/absence';
import { sticky } from '../../../constants/helperCssRules';
import {
  APP_PAGES,
  PARAMS,
  PEOPLE_ABSENCE_REQUESTS_DEFAULT_PARAMS,
} from '../../../constants/pages';
import {
  API_ABSENCE_REQUESTS,
  API_ABSENCE_REQUESTS_OWN,
  API_ABSENCE_ADJUSTMENT,
  api_absence_request,
  api_absence_request_own,
  api_absence_request_delete_own,
  api_absence_request_status,
  api_user_absences,
  api_absence_breakdown,
} from '../../../constants/apiRoutes';
import { ABSENCE_REQUEST_STATUS_TYPES } from '../../../constants/statuses';
import { personAbsencesSelector } from '../../../store/selectors/personAbsencesSelector';
import { personSelector } from '../../../store/selectors/personSelector';
import {
  getPersonAbsences,
  getPersonAbsenceRequests,
  getPersonUpcomingTimeOff,
  clearPersonAbsences,
  getPersonBalanceTimeline,
} from '../../../store/modules/personAbsences';
import http from '../../../utility/http';
import { hasScopedAuthPermissionCheck } from '../../../utility/permissions';
import { useTranslations } from '../../../utility/useTranslations';
import { useCustomEffect } from '../../../utility/hooks';
import { showSuccessMessage, hasNextPage } from '../../../utility/uiUtils';
import {
  isUserDeactivated,
  isArrayEmpty,
  isArray,
  getItemById,
} from '../../../utility/helpers';
import { getYearFromDate } from '../../../utility/dateUtils';
import {
  prepareUserAbsences,
  prepareManageAbsenceRequestFields,
  prepareManageAbsenceRequestInitialData,
  prepareTranslatedAbsenceRequestStatuses,
  prepareAbsenceRequestMenuItems,
  prepareYearOptions,
  prepareUserAvailableAbsenceTypes,
  prepareWorkdays,
} from '../../../utility/absence';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import {
  BALANCE_FIELDS,
  BALANCE_INITIAL_DATA,
  prepareUserRollOverBalanceInitialData,
  prepareUserRollOverBalanceField,
  prepareUserUpcomingTimeOffTableHeaders,
  prepareUserUpcomingTimeOffTableData,
  prepareUserAbsenceRequestsTableHeaders,
  prepareUserAbsenceRequestsTableActionsHeader,
  prepareUserAbsenceRequestsTableData,
  prepareUserBalanceTimelineTableData,
  prepareUserBalanceTimelineTableHeaders,
  isSameAbsenceSetCheck,
} from './config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    header: {
      paddingBottom: spacing(4),
      ...sticky(primary.white, 170),
    },
    description: {
      marginBottom: spacing(4),
    },
    filterSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'end',
    },
    title: {
      marginRight: spacing(4),
    },
    year: {
      minWidth: 100,
      width: 100,
    },
    yearMenu: {
      zIndex: 1000,
    },
    disabledUserAction: {
      cursor: 'not-allowed',
    },
    section: {
      marginTop: spacing(10),
    },
    sectionSpacing: {
      marginBottom: spacing(4),
    },
    sectionDescription: {
      marginBottom: spacing(6),
    },
    form: {
      display: 'grid',
      gridColumnGap: 12,
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr)) 85px',
      gridTemplateAreas: `
        "from to amount"
        "absence absence absence"
        "reason reason reason"
        "description description description"
      `,
    },
    reasonField: {
      gridArea: 'reason',
    },
    absenceField: {
      gridArea: 'absence',
    },
    fromField: {
      gridArea: 'from',
    },
    toField: {
      gridArea: 'to',
    },
    amountField: {
      gridArea: 'amount',
    },
    descriptionField: {
      gridArea: 'description',
    },
    tableHeader: {
      borderRadius: 4,
    },
    tableHeaderCell: {
      color: primary.indigo2,
      padding: spacing(1.5, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(1.5, 2),
      },
    },
    tableRowCell: {
      padding: spacing(4, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(4, 2),
      },
    },
    cellValue: {
      color: primary.indigo,
    },
    descriptionCell: {
      '-webkit-line-clamp': 'none',
    },
    actionsCell: {
      overflow: 'visible',
      paddingLeft: 0,
      paddingTop: spacing(0),
      paddingBottom: spacing(0),
      paddingRight: spacing(2),

      [breakpoints.up('xLg')]: {
        paddingRight: spacing(2),
      },
    },
    icon: {
      cursor: 'default',
    },
    actionsWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    statusActions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 32px)',
      gridColumnGap: 4,
    },
    dotsMenu: {
      marginLeft: spacing(1),
    },
    numberOfDaysField: {
      width: 75,
    },
    wrapper: {
      marginTop: spacing(6),
    },
    labelWrapperBlock: {
      display: 'flex',
      marginBottom: spacing(6),
      marginTop: spacing(6),
      height: 30,
      borderBottom: `1px solid ${primary.bluish5}`,
    },
    selectedLabelWrapper: {
      borderBottom: `4px solid ${primary.blue1}`,
      paddingBottom: spacing(0.5),
    },
    buttonWrapper: {
      height: spacing(6),
      marginRight: spacing(6),
      textTransform: 'none',
      cursor: 'pointer',
    },
    changedSetInfo: {
      backgroundColor: primary.bluish8,
      borderRadius: 8,
      display: 'flex',
      width: 'fit-content',
      padding: spacing(4),
      marginBottom: spacing(6),
    },
  })
);

const PeopleAbsencesPage = ({
  auth,
  accessControl,
  dispatch,
  organizationSettings,
  ...rest
}) => {
  const { id } = useParams();
  const userId = (!Number.isNaN(Number(id)) && +id) || -1;
  const { page: defaultPage } = PEOPLE_ABSENCE_REQUESTS_DEFAULT_PARAMS;

  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PEOPLE_ABSCENCES);
  const { CURRENT_YEAR_ADJUSTMENT_DAYS, TOTAL_PREVIOUS_YEAR, HAS_REASONS } =
    ABSENCE_FIELD_NAMES;
  const { ABSENCE, ABSENCE_FROM, ABSENCE_TO, DESCRIPTION, USER, REASON } =
    ABSENCE_REQUEST_FIELD_NAMES;

  const user = useSelector(personSelector);
  const { absences, requests, balanceTimeline, upcomingTimeOff } = useSelector(
    personAbsencesSelector
  );
  const yearOptions = prepareYearOptions();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isManageRequestOpened, setIsManageRequestOpened] = useState(false);
  const [requestsPage, setRequestsPage] = useState(1);
  const [balanceTimelinePage, setBalanceTimelinePage] = useState(1);
  const [upcomingTimeOffPage, setUpcomingTimeOffPage] = useState(1);
  const [isDeleteRequestOpened, setIsDeleteRequestOpened] = useState(false);
  const [absenceRequest, setAbsenceRequest] = useState(null);
  const [isApproveRequestOpened, setIsApproveRequestOpened] = useState(false);
  const [isRejectRequestOpened, setIsRejectRequestOpened] = useState(false);
  const [isEditBalanceOpenedPreviousYear, setIsEditBalanceOpenedPreviousYear] =
    useState(false);
  const [isEditBalanceOpenedCurrentYear, setIsEditBalanceOpenedCurrentYear] =
    useState(false);
  const [absencesByYear, setAbsencesByYear] = useState([]);
  const [absence, setAbsence] = useState(null);
  const [prevYearAbsence, setPrevYearAbsence] = useState(null);
  const [selectedYear, setSelectedYear] = useState(yearOptions[1].value);
  const [isRequestsTab, setIsRequestsTab] = useState(true);

  const canManage = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.ABSENCE_REQUESTS_MANAGE,
    accessControl,
    auth.id,
    userId
  );
  const isDoubleApprove = organizationSettings.absence_double_approval;
  const tabDescription = isRequestsTab
    ? translations.requests.description
    : translations.timeline.description;
  const noResultsMessage = isRequestsTab
    ? translations.noRequests
    : translations.noTimeline;
  const isCurrentUser = user?.id === auth.id;
  const isDeactivatedUser = isUserDeactivated(user);
  const presentYear = getYearFromDate(new Date());
  const isSelectedPresentYear = presentYear === selectedYear;
  const canRequestAbsence =
    canManage || (isCurrentUser && isSelectedPresentYear);
  const userAbsences = prepareUserAbsences(absences);
  const userAbsencesByYear = prepareUserAvailableAbsenceTypes(
    prepareUserAbsences(absencesByYear)
  );
  const isSameSet = isSameAbsenceSetCheck(userAbsences, userAbsencesByYear);

  const requestStatuses = prepareTranslatedAbsenceRequestStatuses(
    translations.requestStatuses
  );
  const manageRequestInitialData = prepareManageAbsenceRequestInitialData(
    absenceRequest,
    user
  );
  const manageRequestFields = prepareManageAbsenceRequestFields(
    classes,
    selectedYear,
    !!absenceRequest
  );
  const upcomingTimeOffTableHeaders = prepareUserUpcomingTimeOffTableHeaders(
    translations.table,
    classes
  );
  const rollOverBalanceInitialData = prepareUserRollOverBalanceInitialData(
    absence,
    prevYearAbsence
  );
  const rollOverBalanceField = prepareUserRollOverBalanceField(classes);
  const absenceRequestsTableHeaders = prepareUserAbsenceRequestsTableHeaders(
    classes,
    translations.table
  );
  const absenceRequestsTableActions =
    prepareUserAbsenceRequestsTableActionsHeader(classes, translations.table);
  const balanceTimelineTableHeaders = prepareUserBalanceTimelineTableHeaders(
    classes,
    translations.table
  );
  const hasLoadMoreRequests = hasNextPage(requests);
  const hasLoadMoreBalances = hasNextPage(balanceTimeline);
  const hasLoadMoreTimeOff = hasNextPage(upcomingTimeOff);

  const handleGetRequests = ({ newPage, isLoadMore } = {}) => {
    const params = {
      [PARAMS.PAGE]: newPage,
      [PARAMS.YEAR]: selectedYear,
    };

    return getPersonAbsenceRequests(dispatch, userId, !!isLoadMore, params);
  };

  const handleGetAbsencesByYear = year => {
    const params = {
      [PARAMS.YEAR]: year,
    };
    return http
      .get(api_user_absences(userId), { params })
      .then(({ data }) => data);
  };

  const onChangeYear = async year => {
    const data = await handleGetAbsencesByYear(year);
    setSelectedYear(year);
    setAbsencesByYear(data);
  };

  const onLoadMoreRequests = () => {
    setRequestsPage(requestsPage + 1);
    handleGetRequests({
      newPage: requestsPage + 1,
      isLoadMore: true,
    });
  };

  const handleGetBalanceTimeline = ({ newPage, isLoadMore } = {}) => {
    const params = {
      [PARAMS.PAGE]: newPage,
      [PARAMS.YEAR]: selectedYear,
    };

    return getPersonBalanceTimeline(dispatch, userId, !!isLoadMore, params);
  };

  const onLoadMoreBalances = () => {
    setBalanceTimelinePage(balanceTimelinePage + 1);
    handleGetBalanceTimeline({
      newPage: balanceTimelinePage + 1,
      isLoadMore: true,
    });
  };

  const handleGetUpcomingTimeOff = ({ newPage, isLoadMore } = {}) => {
    const params = {
      [PARAMS.PAGE]: newPage,
    };
    return getPersonUpcomingTimeOff(dispatch, userId, !!isLoadMore, params);
  };

  const onLoadMoreUpcomingTimeOff = () => {
    setUpcomingTimeOffPage(upcomingTimeOffPage + 1);
    handleGetUpcomingTimeOff({
      newPage: upcomingTimeOffPage + 1,
      isLoadMore: true,
    });
  };

  const handleOpenManageRequest = () => setIsManageRequestOpened(true);

  const handleOpenEditRequest = currentRequest => {
    setIsManageRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseManageRequest = () => {
    setIsManageRequestOpened(false);
    setAbsenceRequest(null);
  };

  const getTableData = useCallback(async () => {
    if (isRequestsTab) {
      setRequestsPage(defaultPage);
      await handleGetRequests({
        newPage: defaultPage,
        isLoadMore: false,
      });
    } else {
      setBalanceTimelinePage(defaultPage);
      await handleGetBalanceTimeline({
        newPage: defaultPage,
        isLoadMore: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, isRequestsTab]);

  const onSaveRequest = async values => {
    const isOwnRequest = values.user.id === auth.id;
    const absenceRequestDates = prepareWorkdays(
      values[ABSENCE_FROM],
      values[ABSENCE_TO]
    );

    const data = {
      [ABSENCE]: values[ABSENCE].id,
      [DESCRIPTION]: values[ABSENCE][HAS_REASONS] ? '' : values[DESCRIPTION],
      [REASON]: values[ABSENCE][HAS_REASONS] ? values[REASON] : '',
      [PARAMS.ABSENCE_REQUEST_DATES]: absenceRequestDates,
      ...(canManage || absenceRequest ? { [USER]: userId } : {}),
    };

    if (absenceRequest) {
      await http.patch(
        isOwnRequest && !canManage
          ? api_absence_request_own(absenceRequest.id)
          : api_absence_request(absenceRequest.id),
        data
      );
      showSuccessMessage(
        translations.manageAbsenceRequest.successMessages.edit
      );
      setUpcomingTimeOffPage(defaultPage);
      handleGetUpcomingTimeOff({
        newPage: defaultPage,
      });
      getTableData();
      return getPersonAbsences(dispatch, userId, presentYear);
    }

    await http.post(
      isOwnRequest && !canManage
        ? API_ABSENCE_REQUESTS_OWN
        : API_ABSENCE_REQUESTS,
      data
    );
    showSuccessMessage(translations.manageAbsenceRequest.successMessages.add);
    setUpcomingTimeOffPage(defaultPage);
    handleGetUpcomingTimeOff({
      newPage: defaultPage,
    });
    getTableData();
    return getPersonAbsences(dispatch, userId, presentYear);
  };

  const handleOpenDeleteRequest = currentRequest => {
    setIsDeleteRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseDeleteRequest = () => {
    setIsDeleteRequestOpened(false);
  };

  const onDeleteRequest = async () => {
    try {
      const isOwnRequest = absenceRequest.user.id === auth.id;
      await http.delete(
        isOwnRequest && !canManage
          ? api_absence_request_delete_own(absenceRequest.id)
          : api_absence_request(absenceRequest.id)
      );
      showSuccessMessage(translations.deleteRequestDialog.deleteSuccess);
      setUpcomingTimeOffPage(defaultPage);
      setRequestsPage(defaultPage);
      handleGetUpcomingTimeOff({
        newPage: defaultPage,
      });
      handleGetRequests({
        newPage: defaultPage,
      });
      getPersonAbsences(dispatch, userId, presentYear);
    } finally {
      setIsDeleteRequestOpened(false);
      setAbsenceRequest(null);
      setIsManageRequestOpened(false);
    }
  };

  const handleOpenApproveRequest = currentRequest => () => {
    setIsApproveRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseApproveRequest = () => {
    setIsApproveRequestOpened(false);
    setAbsenceRequest(null);
  };

  const onApproveRequest = async () => {
    try {
      await http.patch(api_absence_request_status(absenceRequest.id), {
        [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.APPROVED,
      });
      showSuccessMessage(translations.approveRequestDialog.approveSuccess);
      setUpcomingTimeOffPage(defaultPage);
      setRequestsPage(defaultPage);
      handleGetUpcomingTimeOff({
        newPage: defaultPage,
      });
      handleGetRequests({
        newPage: defaultPage,
      });
      getPersonAbsences(dispatch, userId, presentYear);
    } finally {
      handleCloseApproveRequest();
    }
  };

  const handleOpenRejectRequest = currentRequest => () => {
    setIsRejectRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseRejectRequest = () => {
    setIsRejectRequestOpened(false);
    setAbsenceRequest(null);
  };

  const onRejectRequest = async () => {
    try {
      await http.patch(api_absence_request_status(absenceRequest.id), {
        [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.REJECTED,
      });
      showSuccessMessage(translations.rejectRequestDialog.rejectSuccess);
      setRequestsPage(defaultPage);
      handleGetRequests({
        newPage: defaultPage,
      });
    } finally {
      handleCloseRejectRequest();
    }
  };

  const handleOpenEditBalancePreviousYear = userAbsence => async () => {
    const data = await handleGetAbsencesByYear(presentYear - 1);
    const prevAbsence = getItemById(data, userAbsence[ABSENCE], ABSENCE);

    setIsEditBalanceOpenedPreviousYear(true);
    setAbsence(userAbsence);
    setPrevYearAbsence(prevAbsence || null);
  };

  const handleCloseEditBalancePreviousYear = () => {
    setIsEditBalanceOpenedPreviousYear(false);
    setAbsence(null);
    setPrevYearAbsence(null);
  };

  const onSaveBalancePreviousYear = async values => {
    await http.patch(api_user_absences(userId), {
      [ABSENCE]: absence[ABSENCE],
      [TOTAL_PREVIOUS_YEAR]: +values[TOTAL_PREVIOUS_YEAR],
    });
    showSuccessMessage(
      translations.updateAbsenceBalance.successMessages.editBalancePreviousYear
    );
    if (!isRequestsTab && selectedYear === presentYear - 1) {
      getTableData();
    }
    getPersonAbsences(dispatch, userId, presentYear);
  };

  const handleOpenEditBalanceCurrentYear = userAbsence => () => {
    setIsEditBalanceOpenedCurrentYear(true);
    setAbsence(userAbsence);
  };

  const handleCloseEditBalanceCurrentYear = () => {
    setIsEditBalanceOpenedCurrentYear(false);
    setAbsence(null);
  };

  const onSaveBalanceCurrentYear = async values => {
    await http.post(API_ABSENCE_ADJUSTMENT, {
      [USER]: userId,
      [ABSENCE]: absence[ABSENCE],
      [DESCRIPTION]: values[DESCRIPTION],
      [CURRENT_YEAR_ADJUSTMENT_DAYS]: +values[CURRENT_YEAR_ADJUSTMENT_DAYS],
    });
    showSuccessMessage(
      translations.updateAbsenceBalance.successMessages.editBalanceCurrentYear
    );
    getPersonAbsences(dispatch, userId, presentYear);
    if (!isRequestsTab && isSelectedPresentYear) {
      getTableData();
    }
  };

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  const getInitialData = useCallback(async () => {
    try {
      const [balanceResponse] = await Promise.all([
        getPersonAbsences(dispatch, userId, presentYear),
        getPersonUpcomingTimeOff(dispatch, userId, false),
      ]);
      setAbsencesByYear(balanceResponse);
    } finally {
      setIsInitialLoad(false);
    }
  }, [dispatch, userId, presentYear]);

  const cleanup = useCallback(() => {
    dispatch(clearPersonAbsences());
  }, [dispatch]);

  useCustomEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const renderStatus = currentStatus => (
    <StatusIndicator status={currentStatus} isDoubleApprove={isDoubleApprove} />
  );

  const renderAbsence = currentType => (
    <Tooltip
      customIconClass={classes.icon}
      text={currentType.name}
      icon={currentType.absenceIcon}
    />
  );

  const handleOpenReport = request => {
    const fileUrl = request?.pdf_report?.file;
    if (fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderRequestTableActions = (
    request,
    hasManageRequestButtons,
    hasEdit,
    hasDelete,
    hasOpenReport
  ) => {
    const menuItems = prepareAbsenceRequestMenuItems(
      translations.menuItems,
      request,
      hasEdit,
      hasDelete,
      hasOpenReport,
      handleOpenEditRequest,
      handleOpenDeleteRequest,
      handleOpenReport
    );

    return (
      <div className={classes.actionsWrapper}>
        {hasManageRequestButtons && (
          <div className={classes.statusActions}>
            <ActionButton
              type={ACTION_BUTTON_TYPES.CLOSE}
              tooltipText={translations.menuItems.reject}
              onClickHandler={handleOpenRejectRequest(request)}
              isDisabled={isDeactivatedUser}
              disabledTooltipText={
                translations.rejectRequestAbsenceButtonDisabled
              }
              isSquared
            />
            <ActionButton
              type={ACTION_BUTTON_TYPES.ACCEPT}
              tooltipText={translations.menuItems.approve}
              onClickHandler={handleOpenApproveRequest(request)}
              isDisabled={isDeactivatedUser}
              disabledTooltipText={
                translations.approveRequestAbsenceButtonDisabled
              }
              isSquared
            />
          </div>
        )}
        {(hasEdit || hasDelete || hasOpenReport) && (
          <DotsMenu
            className={classes.dotsMenu}
            menuItems={menuItems}
            isVerticalMenu
          />
        )}
      </div>
    );
  };

  const renderUser = persons => {
    return (
      <ListWithTooltip
        items={persons}
        asyncListProps={{
          position: 'top',
          isDisabled: true,
          shouldDisablePortal: false,
        }}
        isUserList
        isReadOnly
      />
    );
  };

  const renderDescription = description => {
    return <RichTextPreview variant="body1" text={description} />;
  };

  const getBreakdownData = absenceId => {
    const params = {
      year: presentYear,
      absence: absenceId,
    };
    return http.get(api_absence_breakdown(userId), { params });
  };

  const upcomingTimeOffTableData = prepareUserUpcomingTimeOffTableData(
    translations.table,
    upcomingTimeOff?.results || [],
    userAbsences,
    renderAbsence
  );

  const absenceRequestsTableData = prepareUserAbsenceRequestsTableData(
    translations.table,
    auth,
    requests?.results || [],
    requestStatuses,
    isDeactivatedUser,
    canManage,
    isCurrentUser,
    isDoubleApprove,
    renderAbsence,
    renderStatus,
    renderRequestTableActions
  );

  const timelineTableData = prepareUserBalanceTimelineTableData(
    balanceTimeline?.results || [],
    renderUser,
    renderAbsence,
    renderDescription
  );

  const hasTabResults =
    (isRequestsTab && !isArrayEmpty(absenceRequestsTableData)) ||
    (!isRequestsTab && !isArrayEmpty(timelineTableData));

  return (
    <PeoplePageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      userId={userId}
      dispatch={dispatch}
      organizationSettings={organizationSettings}
      shouldPassProps={false}
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.header}>
            <Typography className={classes.description} variant="body2">
              {translations.description}
            </Typography>
            <SectionTitle
              className={classes.title}
              variant="h1"
              title={translations.timeOff}
            />
          </div>
          <AbsenceCards
            translations={translations.absenceCards}
            absences={userAbsences}
            canManageBalance={canManage && !isDeactivatedUser}
            getBreakdownData={getBreakdownData}
            onManageBalancePreviousYear={handleOpenEditBalancePreviousYear}
            onManageBalanceCurrentYear={handleOpenEditBalanceCurrentYear}
          />
          <NotificationCard
            title={translations.noResults.title}
            content={translations.noResults.message}
            shouldFade={isArrayEmpty(absences)}
          />
          <SectionTitle
            className={classNames(classes.section, classes.sectionSpacing)}
            variant="h4"
            title={translations.upcomingTimeOffSection}
          />
          {isArray(upcomingTimeOff.results) &&
            !isArrayEmpty(upcomingTimeOff.results) && (
              <GridTable
                translations={translations.table}
                customHeaderClass={classes.tableHeader}
                headers={upcomingTimeOffTableHeaders}
                rows={upcomingTimeOffTableData}
                hasLoadMore={hasLoadMoreTimeOff}
                onLoadMore={onLoadMoreUpcomingTimeOff}
              />
            )}
          <NotificationCard
            content={translations.noUpcomingTimeOff}
            shouldFade={isArrayEmpty(upcomingTimeOffTableData)}
          />
          <div className={classes.filterSection}>
            <SelectField
              className={classNames(classes.year, {
                [classes.wrapper]: !hasLoadMoreTimeOff,
              })}
              menuClass={classes.yearMenu}
              label={translations.year}
              value={selectedYear}
              options={yearOptions}
              onChange={onChangeYear}
              isSearchDisabled
            />
            {canRequestAbsence &&
              !isArrayEmpty(absences) &&
              !isArrayEmpty(userAbsencesByYear) && (
                <ConditionalTooltip
                  className={classes.disabledUserAction}
                  message={translations.requestAbsenceButtonDisabled}
                  addTooltip={isDeactivatedUser}
                >
                  <CustomButton
                    type="addRoundedNew"
                    disabled={isDeactivatedUser}
                    onClick={handleOpenManageRequest}
                  >
                    {canManage
                      ? translations.addAbsenceButton
                      : translations.requestAbsenceButton}
                  </CustomButton>
                </ConditionalTooltip>
              )}
          </div>
          <div className={classes.labelWrapperBlock}>
            <div
              className={classNames(classes.buttonWrapper, {
                [classes.selectedLabelWrapper]: isRequestsTab,
              })}
              onClick={() => setIsRequestsTab(true)}
            >
              <Typography variant="h4">
                {translations.requests.title}
              </Typography>
            </div>
            <div
              className={classNames(classes.buttonWrapper, {
                [classes.selectedLabelWrapper]: !isRequestsTab,
              })}
              onClick={() => setIsRequestsTab(false)}
            >
              <Typography variant="h4">
                {translations.timeline.title}
              </Typography>
            </div>
          </div>
          {hasTabResults && (
            <Typography className={classes.sectionDescription} variant="body2">
              {tabDescription}
            </Typography>
          )}
          {hasTabResults && !isSameSet && (
            <Typography className={classes.changedSetInfo} variant="subtitle2">
              {translations.changedSetInfo}
            </Typography>
          )}
          {isRequestsTab && !isArrayEmpty(absenceRequestsTableData) && (
            <GridTable
              translations={translations.table}
              customHeaderClass={classes.tableHeader}
              headers={absenceRequestsTableHeaders}
              headerActions={absenceRequestsTableActions}
              rows={absenceRequestsTableData}
              hasLoadMore={hasLoadMoreRequests}
              onLoadMore={onLoadMoreRequests}
            />
          )}
          {!isRequestsTab && !isArrayEmpty(timelineTableData) && (
            <GridTable
              translations={translations.table}
              customHeaderClass={classes.tableHeader}
              headers={balanceTimelineTableHeaders}
              rows={timelineTableData}
              hasLoadMore={hasLoadMoreBalances}
              onLoadMore={onLoadMoreBalances}
            />
          )}
          <NotificationCard
            content={noResultsMessage}
            shouldFade={!hasTabResults}
          />
          <CustomFormDrawer
            customFormClass={classes.form}
            translations={translations.manageAbsenceRequest}
            isOpened={isManageRequestOpened}
            initialData={manageRequestInitialData}
            isInitialValid={!!absenceRequest}
            fields={manageRequestFields}
            absences={userAbsencesByYear}
            onDelete={handleOpenDeleteRequest}
            onClose={handleCloseManageRequest}
            onSave={onSaveRequest}
            hasCancelButton
          />
          <CustomFormDrawer
            translations={translations.updateAbsenceBalance}
            titleText={translations.updateAbsenceBalance.balancePreviousYear}
            isOpened={isEditBalanceOpenedPreviousYear}
            initialData={rollOverBalanceInitialData}
            isInitialValid={!!absence}
            fields={rollOverBalanceField}
            onClose={handleCloseEditBalancePreviousYear}
            onSave={onSaveBalancePreviousYear}
            hideDelete
            hasCancelButton
          />
          <CustomFormDrawer
            translations={translations.updateAbsenceBalance}
            titleText={translations.updateAbsenceBalance.balanceCurrentYear}
            isOpened={isEditBalanceOpenedCurrentYear}
            initialData={BALANCE_INITIAL_DATA}
            fields={BALANCE_FIELDS}
            onClose={handleCloseEditBalanceCurrentYear}
            onSave={onSaveBalanceCurrentYear}
            hideDelete
            hasCancelButton
          />
          <AlertDialog
            translations={translations.deleteRequestDialog}
            isOpened={isDeleteRequestOpened}
            onClose={handleCloseDeleteRequest}
            onConfirm={onDeleteRequest}
            isWarning
          />
          <AlertDialog
            translations={translations.approveRequestDialog}
            isOpened={isApproveRequestOpened}
            onClose={handleCloseApproveRequest}
            onConfirm={onApproveRequest}
            isWarning
          />
          <AlertDialog
            translations={translations.rejectRequestDialog}
            isOpened={isRejectRequestOpened}
            onClose={handleCloseRejectRequest}
            onConfirm={onRejectRequest}
            isWarning
          />
        </div>
      )}
    </PeoplePageContainer>
  );
};

PeopleAbsencesPage.propTypes = {
  auth: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  organizationSettings: PropTypes.object.isRequired,
};

export default memo(PeopleAbsencesPage);
